import useBoardContentObject from './useBoardContentObject';
import useBoardContentWidget from './useBoardContentWidget';
import { IBoardObject } from '../types/IBoardObject';
import { IBoardObjectNode } from '../types/IBoardObjectNode';
import { IBoardWidget } from '../types/IBoardWidget';
import { formatDate } from '@progress/kendo-intl';
import { ITimePeriod } from '../types/ITimePeriod';
import { IBoardMeasure } from '../types/IBoardMeasure';
import { ICurrency } from '../types/ICurrency';
import { IBoardScenario } from '../types/IBoardScenario';
import { nodeCombinedValues, nodeValues } from '../../services/analysisContentService';
import { IWidgetData } from '../types/IWidgetData';

const useBoardContentWidgetData = () => {
  const {
    findNode,
    childNodesByPassGroups,
    nodesFor,
    childNodes,
    nodesForType,
    nodesForCategoryTypeLevel,
  } = useBoardContentObject();
  const {nodesExcludedForObject, widgetSetup} = useBoardContentWidget();

  const defineNodesForParent = (focusedObject: IBoardObject, nodeId: number): IBoardObjectNode[] | undefined => {
    let nodes: IBoardObjectNode[] = [];
    let parentNode: IBoardObjectNode | null = null;

    if (!focusedObject) return;

    parentNode = findNode(focusedObject.nodes, nodeId);

    if (parentNode) {
      nodes = childNodesByPassGroups(focusedObject.nodes, nodeId);
    }

    return nodes;
  };

  const _removeExcluded = (widget: IBoardWidget, focusedObject: IBoardObject, nodesToCheck: any): void => {
    let excludedNodes: any = nodesExcludedForObject(widget, focusedObject.objectPath || '');

    for (let i = 0; i < excludedNodes.nodes.length; i++) {
      let exnode = excludedNodes.nodes[i];

      for (let j = 0; j < nodesToCheck.length; j++) {
        let nodes = nodesToCheck[j];
        let remove = -1;
        nodes.find((item: any, index: number) => {
          let isEq = item.id === exnode.nodeId;
          if (isEq) remove = index;
          return isEq;
        });
        if (remove > -1) nodes.splice(remove, 1);
      }
    }
  };

  const defineNodes = (widget: IBoardWidget, focusedObject: IBoardObject): { nodes: IBoardObjectNode[], groups: IBoardObjectNode[] } => {
    let nodes: IBoardObjectNode[] = [];
    let groups: IBoardObjectNode[] = [];

    let setup = widgetSetup(widget);

    if (setup.sliceGroup) {
      if (setup.sliceGroupLevel) {
        nodes = defineNodesForCategoryTypeLevel(widget, setup.sliceGroup, setup.sliceGroupLevel, focusedObject);
      } else {
        const {definedNodes, definedGroups} = _defineNodesForGroup(nodes, widget, focusedObject);
        nodes = definedNodes;
        groups = definedGroups;
      }
    } else if (setup.sliceType) {
      nodes = _defineNodesForSliceType(nodes, widget, focusedObject);
    } else {
      nodes = focusedObject?.nodesOfObject?.slice();
      _removeExcluded(widget, focusedObject, [nodes]);
    }

    return {nodes, groups};
  };

  const _defineNodesForGroup = (nodes: IBoardObjectNode[], widget: IBoardWidget, focusedObject: IBoardObject): { definedNodes: IBoardObjectNode[], definedGroups: IBoardObjectNode[] } => {
    let groups: IBoardObjectNode[] = [];
    let setup = widgetSetup(widget);

    if (!nodesFor(focusedObject.nodes, focusedObject.objectId)) {
      return {definedNodes: nodes, definedGroups: groups};
    }

    groups = nodesFor(focusedObject.nodes, setup.sliceGroup) || [];
    _removeExcluded(widget, focusedObject, [groups]);

    if (groups) {
      for (let i = 0; i < groups.length; i++) {
        let group = groups[i];
        if (group.id && childNodes(focusedObject.nodes, group.id)) {
          let node = group.id && childNodes(focusedObject.nodes, group.id);
          nodes.push.apply(nodes, node || []);
        }
      }
    }

    return {definedNodes: nodes, definedGroups: groups};
  };

  const _defineNodesForSliceType = (nodes: IBoardObjectNode[], widget: IBoardWidget, focusedObject: IBoardObject): IBoardObjectNode[] => {
    let setup = widgetSetup(widget);

    nodes = nodesForType(nodes, setup.sliceType);
    _removeExcluded(widget, focusedObject, [nodes]);

    return nodes;
  };

  const defineNodesForCategoryTypeLevel = (widget: IBoardWidget, categoryTypeId: number, categoryLevel: number, focusedObject?: IBoardObject): IBoardObjectNode[] => {
    let nodes: IBoardObjectNode[];

    nodes = nodesForCategoryTypeLevel(focusedObject?.nodes || [], categoryTypeId, categoryLevel);

    let focusedObjectPath = focusedObject && focusedObject.objectPath;
    let exclude = nodesExcludedForObject(widget, focusedObjectPath || '');

    for (let i = nodes.length - 1; i > -1; i--) {
      let parent: any = nodes[i];
      while (parent) {
        if (parent.id && exclude.idLookup[parent.id]) {
          nodes.splice(i, 1);
          break;
        }
        parent = parent.parentNode;
      }
    }

    return nodes;
  };

  const _formatPeriod = (date: Date) => {
    return formatDate(date, 'YYYY-MM-dd').split('T')[0];
  }

  const fetchData = (widget: IBoardWidget,
                     nodes: IBoardObjectNode[],
                     measures: IBoardMeasure[],
                     groups: IBoardObjectNode[],
                     focusedPeriod: ITimePeriod,
                     focusedCurrency: string,
                     includeDescendants: boolean) => {
    let setup = widgetSetup(widget);

    if (setup.sliceGroup) {
      return Promise.resolve(
        nodeValues(
          widget.contentId || 0,
          nodes,
          groups,
          measures,
          _formatPeriod(focusedPeriod.endDate),
          setup.singlePeriod,
          focusedCurrency,
          widget.investorObjectId || 0,
          includeDescendants
        ),
      );
    }

    return Promise.resolve(
      nodeValues(
        widget.contentId || 0,
        nodes,
        null,
        measures,
        _formatPeriod(focusedPeriod.endDate),
        setup.singlePeriod,
        focusedCurrency,
        widget.investorObjectId || 0,
        includeDescendants
      ),
    );
  };

  const fetchNodeData = (widget: IBoardWidget,
                         nodes: IBoardObjectNode[],
                         measures: IBoardMeasure[],
                         focusedPeriod: ITimePeriod,
                         focusedCurrency: string,
                         includeDescendants?: boolean): Promise<IWidgetData[]> => {
    let setup = widgetSetup(widget);

    // return Promise.resolve(
    return nodeValues(
      widget.contentId || 0,
      nodes,
      null,
      measures,
      _formatPeriod(focusedPeriod.endDate),
      setup.singlePeriod,
      focusedCurrency,
      widget.investorObjectId || 0,
      includeDescendants,
    )
    // );
  };

  const fetchCombinedData = (widget: IBoardWidget,
                             nodes: IBoardObjectNode[],
                             groups: IBoardObjectNode[],
                             measures: IBoardMeasure[],
                             staticProperties: any,
                             focusedPeriod: ITimePeriod,
                             focusedCurrency: string,
                             includeDescendants?: boolean) => {
    if (!focusedPeriod) {
      return Promise.resolve(null);
    }

    let setup = widgetSetup(widget);

    if (setup.sliceGroup)
      return nodeCombinedValues(
        widget.contentId || 0,
        null,
        groups,
        measures,
        staticProperties,
        _formatPeriod(focusedPeriod.endDate),
        setup.singlePeriod,
        focusedCurrency,
        widget.investorObjectId || 0,
        includeDescendants,
      );

    return nodeCombinedValues(
      widget.contentId || 0,
      nodes,
      null,
      measures,
      staticProperties,
      _formatPeriod(focusedPeriod.endDate),
      setup.singlePeriod,
      focusedCurrency,
      widget.investorObjectId || 0,
      includeDescendants,
    );
  };

  const generateCurrencies = (widget: IBoardWidget, nodes: IBoardObjectNode[], scenarios: IBoardScenario[]): ICurrency[] => {
    let currencies: ICurrency[] = [];

    let setup = widgetSetup(widget);

    nodes?.forEach((node) => {
      let scenario = scenarios?.find((item) => {
        return item.id === node.scenarioId;
      });
      if (scenario && scenario.currencies) {
        scenario.currencies.forEach((currency) => {
          if (!currencies?.find((item) => {
            return item.currency === currency.currency;
          })) {
            currencies.push(currency);
          }
        });
      }
    });

    if (setup.suppressLocalCurrency) {
      return currencies.filter((curr) => {
        return curr.currency !== '_';
      });
    } else {
      return currencies;
    }
  };

  return {
    defineNodesForParent,
    defineNodesForCategoryTypeLevel,
    defineNodes,
    fetchData,
    fetchNodeData,
    fetchCombinedData,
    generateCurrencies
  }
}

export default useBoardContentWidgetData;